import './Player.styles.css';
import '../../App.css';

import { Avatar } from 'primereact/avatar';
import { ProgressBar } from 'primereact/progressbar';
import { Tooltip } from 'primereact/tooltip';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getPokerRoom } from '../../constants';
import { GameStage } from '../../model/enumeration/GameStage.enum';
import { GameType } from '../../model/enumeration/GameType.enum';
import { Permission } from '../../model/enumeration/Permission.enum';
import { PokerRoom } from '../../model/enumeration/PokerRoom';
import botErrorStore from '../../store/BotErrorStore';
import chatStore from '../../store/ChatStore';
import commonStore from '../../store/CommonStore';
import permissionStore from '../../store/PermissionStore';
import tableStore from '../../store/TableStore';
import userSettingsStore from '../../store/UserSettingsStore';
import { isSelection } from '../../utils/mouse.utils';
import CallTimeClock from '../CallTimeClock/CallTimeClock';
import { CardModel } from '../Card/Card.types';
import MultiCard from '../Card/MultiCard/MultiCard';
import CountryFlag from '../CountryFlag/CountryFlag';
import { formatNumber } from '../Game/table.render.utils';
import ManageBotActions from '../ManageBotActions/ManageBotActions';
import PlayerAction from '../PlayerAction/PlayerAction';
import PlayerInfo from '../PlayerInfo/PlayerInfo';
import TargetEmojiDialog from '../TargetEmoji/Dialog/TargetEmojiDialog';
import getBlinds from './player.render.utils';
import { PlayerProps } from './Player.types';
import PlayerColorSettings from './PlayerColorSettings/PlayerColorSettings';
import PlayerStatus from './PlayerStatus/PlayerStatus';

const getInitProgressValue = (
  turnStartingTime: Date,
  stepDuration: number,
): number => {
  if (!turnStartingTime || turnStartingTime.getTime() === 0) {
    return 100;
  }
  const elapsedTime =
    new Date().valueOf() + commonStore.timeSift - turnStartingTime?.valueOf();
  return Math.min(100, 100 - (elapsedTime / stepDuration) * 100);
};

const Player: FC<PlayerProps> = ({
  player,
  gameStage,
  isActive,
  tablePosition,
  isDealer,
  isPlayerGlowing,
  won,
  loss,
  stepDuration,
  isLast,
  cardsLength,
  tableBB,
  table,
  turnStartingTime,
  isolated,
}) => {
  const [clickTimeout, setClickTimeout] = useState(null);

  const [targetEmojiEvent, setTargetEmojiEvent] =
    useState<React.MouseEvent<HTMLSpanElement>>();

  const [progress, setProgress] = useState(
    getInitProgressValue(turnStartingTime, stepDuration),
  );
  const [isPlayerFlashing, setIsPlayerFlashing] = useState(false);

  const [isManageBotActionsVisible, setIsManageBotActionsVisible] =
    useState(false);

  const [isPlayerInfoVisible, setIsPlayerInfoVisible] = useState(false);

  const [originalBackgroundColor, setOriginalBackgroundColor] = useState(
    'rgba(var(--bs-dark-rgb)',
  );

  const timer = useRef(null);
  const progressBarRef = useRef<ProgressBar>(null);
  const playerRef = useRef<HTMLDivElement>(null);

  const cards = useMemo(() => {
    if (player.cards.length) {
      return player.cards.map(
        card =>
          (card
            ? { ...card, reversed: false }
            : { reversed: true }) as CardModel,
      );
    } else if (gameStage && gameStage !== GameStage.BLINDS) {
      return Array.from(
        { length: cardsLength },
        () => ({ reversed: true } as CardModel),
      );
    } else {
      return [];
    }
  }, [gameStage, player.cards]);

  const image = useMemo(
    () => (player.myBot ? '/assets/bot.svg' : '/assets/player.svg'),
    [player.myBot],
  );

  const hasCards = useMemo(() => cards.some(card => !card.reversed), [cards]);

  const memoizedBlinds = useMemo(
    () => getBlinds(player.blinds),
    [player.blinds],
  );

  const isPokerrrr2 = useMemo(() => getPokerRoom() === PokerRoom.Pokerrrr2, []);

  const isMtt = useMemo(
    () => table.gameType.includes(GameType.MTT),
    [table.gameType],
  );

  const splashProgressBar = useCallback(() => {
    if (progressBarRef.current) {
      const progressBar = progressBarRef.current.getElement();
      let isRed = true;
      progressBar.classList.add('transition-default');
      const toggleColor = (): void => {
        if (isRed) {
          progressBar.style.backgroundColor = 'black';
        } else {
          progressBar.style.backgroundColor = 'maroon';
        }
        isRed = !isRed;
      };
      const interval = setInterval(toggleColor, 500);
      return () => clearInterval(interval);
    }
  }, [progressBarRef]);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (player?.myBot && tableStore.isBotTracked(player.pid) && !isolated) {
      playerElement.classList.add('tracked-frame');
    } else {
      playerElement.classList.remove('tracked-frame');
    }
  }, [tableStore.trackedBotIds.length, player.myBot, isolated]);

  useEffect(() => {
    if (turnStartingTime && !userSettingsStore.settings.table.lightMode) {
      setProgress(getInitProgressValue(turnStartingTime, stepDuration));
    }
  }, [turnStartingTime, stepDuration]);

  useEffect(() => {
    if (userSettingsStore.settings.table.lightMode) {
      return;
    }
    if (!isActive) {
      setProgress(100);
      return;
    }
    const interval = stepDuration / 20;
    timer.current = setInterval(() => {
      setProgress(oldProgress => {
        if (oldProgress <= 0) {
          splashProgressBar();
          clearInterval(timer.current);
          return 0;
        }
        return commonStore.paused ? oldProgress : oldProgress - 5;
      });
    }, interval);

    return () => {
      clearInterval(timer.current);
    };
  }, [
    isActive,
    commonStore.paused,
    userSettingsStore.settings.table.lightMode,
  ]);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement) {
      playerElement.style.backgroundColor = originalBackgroundColor;
    }
  }, [originalBackgroundColor]);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement && !player.sittingOut && !player.folded) {
      if (isActive) {
        setOriginalBackgroundColor('rgba(var(--bs-success-rgb)');
      } else {
        setOriginalBackgroundColor('rgba(var(--bs-dark-rgb)');
      }
    }
  }, [isActive, playerRef, player.sittingOut, player.folded]);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement && player.folded) {
      setOriginalBackgroundColor('rgba(var(--bs-secondary-rgb)');
    }
  }, [player.folded, playerRef.current]);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement && player.sittingOut) {
      setOriginalBackgroundColor('rgba(var(--bs-secondary-rgb)');
    }
  }, [player.sittingOut, playerRef.current]);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement && won > 0 && gameStage === GameStage.GAME_FINISHED) {
      playerElement.classList.add('win-frame');
    } else {
      playerElement.classList.remove('win-frame');
    }
  }, [won, gameStage]);

  useEffect(() => {
    const botHasError = botErrorStore
      .getBotErrors(table.id)
      .map(error => error.pid)
      .includes(player.pid);

    if (!userSettingsStore.settings.table.lightMode) {
      const interval = setInterval(() => {
        if (botHasError) {
          setIsPlayerFlashing(isFlashing => !isFlashing);
        }
      }, 500);

      return () => {
        clearInterval(interval);
      };
    } else {
      setIsPlayerFlashing(
        botHasError && botErrorStore.isBotSplashing(table.id, player.pid),
      );
    }
  }, [
    botErrorStore.splashingBots.length,
    playerRef,
    userSettingsStore.settings.table.lightMode,
  ]);

  useEffect(() => {
    playerRef.current.classList.add('transition-default');
    const defaultOutline = '4px solid transparent';
    if (playerRef && botErrorStore.isBotSplashing(table.id, player.pid)) {
      playerRef.current.style.outline = isPlayerFlashing
        ? '4px solid #a00f0f'
        : defaultOutline;
    } else {
      playerRef.current.style.outline = defaultOutline;
    }
  }, [isPlayerFlashing, playerRef]);

  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement && isPlayerGlowing) {
      const className = 'glowing-frame';
      playerElement.classList.add(className);

      const timeout = setTimeout(() => {
        playerElement.classList.remove(className);
        table.removeGlowingPlayer();
      }, 10000);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isPlayerGlowing]);

  const openBotErrorOverlay = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (botErrorStore.errorExist(table.id, player.pid)) {
      botErrorStore.addLastOpenedError(player);
      botErrorStore.addToggleBotErrorEventMap(table.id, e);
    }
  };

  const handleRightClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): void => {
    if (!player.myBot) {
      event.preventDefault();
      setIsPlayerInfoVisible(true);
    }
    if (player.myBot && !isolated && !isSelection()) {
      event.preventDefault();
      setIsManageBotActionsVisible(true);
    }
  };

  const handleClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): void => {
    if (isSelection()) {
      return;
    }
    if (
      !userSettingsStore.settings.chat.disabled &&
      !isolated &&
      !clickTimeout &&
      permissionStore.hasPermission(Permission.CHAT)
    ) {
      const timeout = setTimeout(() => {
        if (getPokerRoom() === PokerRoom.Xpoker) {
          setTargetEmojiEvent(event);
        } else if (player?.myBot && !isManageBotActionsVisible) {
          chatStore.addSelectedBot(table.id, player);
          chatStore.addToggleChatEventMap(table.id, event);
        }
        setClickTimeout(null);
      }, 300);
      setClickTimeout(timeout);
    }
  };

  const handleDoubleClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): void => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }
    openBotErrorOverlay(event);
  };

  const renderWinLoss = (): React.ReactNode => {
    const isBotLoss = loss > 0 && (player.myBot || isPlayerGlowing);
    const isWin = won > 0;

    return (
      (isWin || isBotLoss) &&
      gameStage === GameStage.GAME_FINISHED && (
        <div>
          <div className="win-loss-summ">
            {isWin ? '+' : '-'}
            {formatNumber(
              commonStore.convertMoney(isWin ? won : loss, tableBB),
            )}
            {commonStore.getIsChipsConvertedToBlinds() && (
              <span className="bb-text">{' BB'}</span>
            )}
          </div>
          <img
            className="win-loss-img"
            src={`/assets/${isWin ? 'win.png' : 'loss.png'}`}
          />
        </div>
      )
    );
  };

  const leaveClassName = useMemo(() => {
    if (player.myBot) {
      if (player.leaveTable) {
        return 'leave-table';
      } else if (player.leaveOnUTG) {
        return 'leave-on-utg';
      }
    }
    return '';
  }, [player.leaveTable, player.leaveOnUTG]);

  return (
    <div
      onClick={event => handleClick(event)}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleRightClick}
      ref={playerRef}
      className={`player-wrapper px-3 p-2 text-center position-absolute
            ${player.sittingOut ? 'sitting-out' : ''} ${
        player.notParticipating ? 'not-participating' : ''
      } ${
        !player.notParticipating && player.firstHand && !player.myBot
          ? 'first-hand'
          : ''
      }`}
      style={
        tablePosition && {
          left: tablePosition.playerX,
          top: tablePosition.playerY,
        }
      }
      data-ignore-outside-click={true}
    >
      <PlayerColorSettings pid={player.pid} />
      {!isolated && (
        <ManageBotActions
          isVisible={isManageBotActionsVisible}
          bot={player}
          tableId={table.id}
          room={table.room}
          onHide={() => setIsManageBotActionsVisible(false)}
        />
      )}
      {!isolated && (
        <PlayerInfo
          isVisible={isPlayerInfoVisible}
          player={player}
          onHide={() => setIsPlayerInfoVisible(false)}
        />
      )}
      {userSettingsStore.settings.table.countryFlag && (
        <div className="country-flag">
          <CountryFlag countryCode={player.country} />
        </div>
      )}
      {player.callTime && (
        <CallTimeClock
          tableId={table.id}
          pid={player.pid}
          country={player.country}
          callTime={player.callTime}
        />
      )}
      <div className="position-relative">
        <PlayerAction pid={player.pid} tableId={table.id} tableBB={tableBB} />
        {(player.myBot || (!player.folded && !player.sittingOut) || hasCards) &&
          !player.notParticipating && (
            <MultiCard
              playerId={player.pid}
              cards={cards}
              cardsPosition={{ cardX: '50%', cardY: '-40px' }}
            />
          )}

        {renderWinLoss()}

        <div className="position-absolute start-0 text-start">
          {isDealer && (
            <img
              className="mt-2"
              height="40"
              width="40"
              src="/assets/dealer-button-clubgg.png"
            />
          )}
          {memoizedBlinds}
          {Number.isFinite(player.actualPrStats?.vpip) &&
            userSettingsStore.settings.table.vpip && (
              <div className="vpip">
                <b>{player.actualPrStats.vpip}</b>
              </div>
            )}
        </div>
        <div
          className="d-inline-block border border-2 rounded-circle border-black p-2 position-relative"
          style={{ backgroundColor: player.myBot ? 'white' : '#bd3c25' }}
        >
          <Avatar image={image} shape="circle" size="xlarge" />
          {isMtt && player.myBot && player.mttPosition && (
            <div className="mtt-position">
              <b>{player.mttPosition}</b>
            </div>
          )}
          {player.myBot && (player.leaveOnUTG || player.leaveTable) && (
            <>
              <i
                className={`leave-icon ${leaveClassName} pi pi-sign-out position-absolute end-0 bottom-0`}
                id={`leave-icon-${player.pid}`}
              />
            </>
          )}
          {isPokerrrr2 && player.myBot && player.waitingRebuy && (
            <>
              <i
                className={`waiting-host-rebuy-icon pi pi-hourglass position-absolute bottom-0`}
                id={`waiting-host-rebuy-icon-${player.pid}`}
              />
              <Tooltip
                className="waiting-host-rebuy-tooltip"
                target={`#waiting-host-rebuy-icon-${player.pid}`}
                position="right"
                content={`Waiting rebuy ${player.waitingRebuyAmount}`}
              />
            </>
          )}
        </div>
        <div className={`player-info ${leaveClassName} p-1`}>
          <p className="mt-1 mb-2">
            <b>{player.nick}</b>
          </p>
          <p className="my-1">{player.pid}</p>
        </div>
        <PlayerStatus
          allIn={player.allIn}
          stack={commonStore.convertMoney(player.stack, tableBB)}
          sittingOut={player.sittingOut}
        />
        {isActive &&
          gameStage !== GameStage.GAME_FINISHED &&
          !isLast &&
          !userSettingsStore.settings.table.lightMode && (
            <ProgressBar
              id={`player-${player.pid}`}
              className="mt-1"
              ref={progressBarRef}
              value={progress}
              showValue={false}
              mode="determinate"
            />
          )}
      </div>
      <TargetEmojiDialog
        toggleEvent={targetEmojiEvent}
        player={player}
        table={table}
        onHide={setTargetEmojiEvent}
      />
    </div>
  );
};

export default Player;
